import {
  AppCard,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiCheck,
  mdiChevronDown,
  mdiClose,
  mdiLoading,
  mdiMinus,
} from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AcceptanceTerm } from "../../../../@types/services/AcceptanceTerm";
import type { Term } from "../../../../@types/services/Term";
import type { TermDocument } from "../../../../@types/services/TermDocument";
import type { Vendor } from "../../../../@types/services/Vendor";
import { AcceptanceTermForm } from "../../../../components/molecules/AcceptanceTermForm";
import { LoadingCentral } from "../../../../components/molecules/LoadingCentral";
import { useAuth } from "../../../../hooks/useAuth";
import { fetchAcceptanceTerms } from "../../../../services/acceptanceTerms";
import {
  fetchActiveTermDocument,
  fetchArchivedTermDocuments,
  fetchTerms,
} from "../../../../services/terms";
import { fetchVendor } from "../../../../services/vendors";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

interface ItemTermProps {
  term: Term;
}

const PendingAcceptanceOfMandatoryTerms = (
  vendor: Vendor,
  term: Term,
): boolean => {
  if (term.term_types.length === 0) {
    return true;
  }

  return term.term_types.some((type: string) => {
    return vendor[type as keyof Vendor];
  });
};

interface ChipAcceptanceTermProps {
  term: Term;
  acceptanceTerm?: AcceptanceTerm | null;
  vendor?: Vendor;
}

const ChipAcceptanceTerm: React.FC<ChipAcceptanceTermProps> = ({
  term,
  acceptanceTerm,
  vendor,
}) => {
  const { t } = useTranslation();

  if (acceptanceTerm === undefined) {
    return <Chip icon={<Icon icon={mdiLoading} spin />} label={t("loading")} />;
  }

  if (acceptanceTerm?.accepted) {
    return (
      <Chip
        icon={<Icon icon={mdiCheck} />}
        label={t("terms.accepted")}
        sx={{
          "& .MuiChip-icon": {
            color: "#1a5f2c",
          },
          "&:hover": {
            backgroundColor: "#a8d1b8",
          },
          backgroundColor: "#c3e4ce",
          color: "#1a5f2c",
        }}
      />
    );
  }

  if (
    vendor &&
    term.mandatory &&
    PendingAcceptanceOfMandatoryTerms(vendor, term)
  ) {
    return (
      <Chip
        icon={<Icon icon={mdiClose} />}
        label={t("terms.pendingAcceptance")}
        sx={{
          "& .MuiChip-icon": {
            color: "#fff",
          },
          "&:hover": {
            backgroundColor: "#cc0e35",
          },
          backgroundColor: "#ff1243",
          color: "#fff",
        }}
      />
    );
  }

  return (
    <Chip
      icon={<Icon icon={mdiMinus} />}
      label={t("terms.notRequired")}
      sx={{
        "& .MuiChip-icon": {
          color: "#fff",
        },
        "&:hover": {
          backgroundColor: "#4d4d4d",
        },
        backgroundColor: "#616161",
        color: "#fff",
      }}
    />
  );
};

interface IndicatorAcceptanceTermProps {
  acceptanceTerm?: AcceptanceTerm | null;
  loadingAcceptanceTerm: boolean;
  lastAcceptanceTerm?: AcceptanceTerm | null;
}

const IndicatorAcceptanceTerm: React.FC<IndicatorAcceptanceTermProps> = ({
  acceptanceTerm,
  loadingAcceptanceTerm,
  lastAcceptanceTerm,
}) => {
  const { t } = useTranslation();

  return !loadingAcceptanceTerm && acceptanceTerm ? (
    <Chip
      label={`${t("terms.acceptedBy", {
        name: acceptanceTerm?.accepted_by?.full_name_with_login,
      })} - ${t("intlDateTime", i18nextParseToDate(acceptanceTerm.updated_at))}`}
      sx={{
        "& .MuiChip-label": {
          padding: "0 12px",
        },
        backgroundColor: "#f5f5f5",
        color: "#616161",
        flexShrink: 0,
        marginTop: 1,
      }}
    />
  ) : (
    lastAcceptanceTerm && (
      <Chip
        label={`${t("terms.lastAcceptedBy", {
          name: lastAcceptanceTerm?.accepted_by?.full_name_with_login,
        })} - ${t("intlDateTime", i18nextParseToDate(lastAcceptanceTerm.updated_at))}`}
        sx={{
          "& .MuiChip-label": {
            padding: "0 12px",
          },
          backgroundColor: "#f5f5f5",
          color: "#616161",
          flexShrink: 0,
          marginTop: 1,
        }}
      />
    )
  );
};

const ItemTerm: React.FC<ItemTermProps> = ({ term }) => {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();

  const [activeTermDocument, setActiveTermDocument] = useState<TermDocument>();
  const [acceptanceTerm, setAcceptanceTerm] = useState<AcceptanceTerm | null>();
  const [lastAcceptanceTerm, setLastAcceptanceTerm] =
    useState<AcceptanceTerm | null>();
  const [internalLoading, setInternalLoading] = useState(false);
  const [loadingAcceptanceTerm, setLoadingAcceptanceTerm] = useState(true);

  const handleFetchLatestArchivedTermDocument =
    useCallback(async (): Promise<void> => {
      fetchArchivedTermDocuments(term.id)
        .then((archivedTermDocuments) => {
          const sortedArchivedTermDocuments = archivedTermDocuments.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          if (
            sortedArchivedTermDocuments.length > 0 &&
            sortedArchivedTermDocuments[0]
          ) {
            const latestArchivedTermDocument = sortedArchivedTermDocuments[0];
            fetchAcceptanceTerms({
              filterTermDocument: {
                termDocumentId: latestArchivedTermDocument.id,
                vendorId: String(user?.vendor?.id),
              },
              order: "desc",
            }).then((response) => {
              if (response.results && response.results.length > 0) {
                setLastAcceptanceTerm(response.results[0]);
              } else {
                setLastAcceptanceTerm(null);
              }
            });
          }
        })
        .finally(() => {
          setInternalLoading(false);
        });
    }, [term.id, user]);

  const handleFetchAcceptanceTerm = useCallback(
    async (termId: Term["id"]) => {
      fetchActiveTermDocument(termId).then((termDocument) => {
        setActiveTermDocument(termDocument);

        fetchAcceptanceTerms({
          filterTermDocument: {
            termDocumentId: termDocument.id,
            vendorId: String(user?.vendor?.id),
          },
          order: "desc",
        })
          .then((response) => {
            if (response.results && response.results.length > 0) {
              setAcceptanceTerm(response.results[response.results.length - 1]);
            } else {
              setAcceptanceTerm(null);
              handleFetchLatestArchivedTermDocument();
            }
          })
          .finally(() => {
            setLoadingAcceptanceTerm(false);
          });
      });
    },
    [handleFetchLatestArchivedTermDocument, user],
  );

  const handleAccepted = useCallback(
    (createdAcceptanceTerm: AcceptanceTerm) => {
      setAcceptanceTerm(createdAcceptanceTerm);
      setLoadingAcceptanceTerm(false);
    },
    [],
  );

  useEffect(() => {
    handleFetchAcceptanceTerm(term.id);
  }, [handleFetchAcceptanceTerm, term.id]);

  const { data: vendor } = useQuery({
    enabled: !!user?.vendor?.id,
    // biome-ignore lint/style/noNonNullAssertion: Vendor ID is checked in enabled
    queryFn: () => fetchVendor(user!.vendor!.id),
    queryKey: ["vendor", user?.vendor?.id],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 1,
  });

  if (internalLoading || !vendor) {
    return <LoadingCentral />;
  }

  return (
    <>
      {PendingAcceptanceOfMandatoryTerms(vendor, term) &&
        term.mandatory &&
        !loadingAcceptanceTerm &&
        !acceptanceTerm?.accepted && (
          <Alert severity="error" sx={{ marginTop: 1 }}>
            {t("terms.alert.requiredAcceptTerm")}
          </Alert>
        )}
      <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary
          aria-controls="terms-content"
          expandIcon={<Icon icon={mdiChevronDown} />}
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            component="span"
            sx={{
              marginRight: 1,
              marginTop: 0.5,
            }}
          >
            {term.name}
          </Typography>

          <ChipAcceptanceTerm
            acceptanceTerm={acceptanceTerm}
            term={term}
            vendor={vendor}
          />
        </AccordionSummary>

        {!!activeTermDocument && (
          <AccordionDetails>
            <Box sx={{ margin: 1 }}>
              <AcceptanceTermForm
                acceptanceTerm={acceptanceTerm}
                onAccepted={handleAccepted}
                term={term}
                termDocument={activeTermDocument}
              />
            </Box>
            <Divider />
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <IndicatorAcceptanceTerm
                acceptanceTerm={acceptanceTerm}
                lastAcceptanceTerm={lastAcceptanceTerm}
                loadingAcceptanceTerm={loadingAcceptanceTerm}
              />

              <iframe
                height="800px"
                loading="lazy"
                src={
                  i18n.language === "en"
                    ? activeTermDocument.attachment_en.url_inline
                    : activeTermDocument.attachment_pt_br.url_inline
                }
                style={{ marginTop: "16px" }}
                title={
                  i18n.language === "en"
                    ? activeTermDocument.attachment_en.filename
                    : activeTermDocument.attachment_pt_br.filename
                }
                width="100%"
              />
              <Typography
                component="span"
                sx={{ color: "text.secondary", marginTop: 1 }}
              >
                {t("terms.lastUpdate")}
                {t(
                  "intlDateTime",
                  i18nextParseToDate(activeTermDocument.updated_at),
                )}
              </Typography>
            </Box>
          </AccordionDetails>
        )}
      </Accordion>
    </>
  );
};

const RouteComponent: React.FC = () => {
  const { t } = useTranslation();

  const terms = Route.useLoaderData();

  const navigate = Route.useNavigate();
  const { refreshUser } = useAuth();

  const [loading, setLoading] = useState(false);

  const handleContinue = useCallback(async () => {
    setLoading(true);
    await refreshUser(true);
    await navigate({ replace: true, to: "/redirect/rails/home" });
    setLoading(false);
  }, [navigate, refreshUser]);

  return (
    <PageContainer maxWidth="lg" title={t("terms.title")}>
      <AppCard>
        {terms.results.map((term) => (
          <ItemTerm key={term.id} term={term} />
        ))}
      </AppCard>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <Button loading={loading} onClick={handleContinue} variant="contained">
          {t("common.continue")}
        </Button>
      </Box>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_protected/onboarding/vendor/terms")({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.vendor?.pending_acceptance_of_mandatory_terms) {
      throw redirect({
        to: "/redirect/rails/home",
      });
    }
  },
  component: RouteComponent,
  loader: () => fetchTerms(),
});
